import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { TOPICS, LEVELS, PASS_RATIO } from "@/lib/topics";
import { getClient, saveClientState } from "@/lib/clientApi";

const ClientContext = createContext(null);

const LEVEL_UNLOCK = {
  new: ["basic"],
  some: ["basic", "intermediate"],
  confident: ["basic", "intermediate", "advanced"],
};

const emptyProgress = (knowledge = "new") => {
  const allowed = LEVEL_UNLOCK[knowledge] || LEVEL_UNLOCK.new;
  const p = {};
  TOPICS.forEach((t) => {
    p[t.id] = {};
    LEVELS.forEach((l, i) => {
      p[t.id][l.id] = { unlocked: i === 0 || allowed.includes(l.id), completed: false, stars: 0, bestScore: 0 };
    });
  });
  return p;
};

const emptyStats = () => ({ xp: 0, coins: 0, bestStreak: 0, gamesPlayed: 0 });

function mergeProgress(saved, knowledge = "new") {
  const base = emptyProgress(knowledge);
  if (saved) {
    TOPICS.forEach((t) => {
      LEVELS.forEach((l) => {
        const prev = saved?.[t.id]?.[l.id];
        if (prev) base[t.id][l.id] = { ...base[t.id][l.id], ...prev, unlocked: base[t.id][l.id].unlocked || prev.unlocked };
      });
    });
  }
  return base;
}

export function ClientProvider({ children }) {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(emptyProgress);
  const [stats, setStats] = useState(emptyStats);
  const [lastResult, setLastResult] = useState(null);
  const stateRef = useRef({ progress: {}, stats: {} });

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const c = await getClient(clientId);
        if (!active) return;
        setClient(c);
        const p = mergeProgress(c.progress, c.knowledge_level || "new");
        const s = { ...emptyStats(), ...(c.stats || {}) };
        setProgress(p);
        setStats(s);
        stateRef.current = { progress: p, stats: s };
      } catch (e) {
        if (active) setClient(false);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [clientId]);

  const persist = useCallback((p, s) => {
    stateRef.current = { progress: p, stats: s };
    saveClientState(clientId, p, s).catch(() => {});
  }, [clientId]);

  const knowledge = client?.knowledge_level || "some";
  const profile = client
    ? { businessName: client.business_name, description: client.business_description, knowledge }
    : null;

  const level = Math.floor((stats.xp || 0) / 100) + 1;

  const recordResult = useCallback((topicId, difficultyId, { correct, total, streak, mode }) => {
    const ratio = total > 0 ? correct / total : 0;
    const passed = ratio >= PASS_RATIO;
    const stars = ratio >= 0.9 ? 3 : ratio >= 0.75 ? 2 : ratio >= PASS_RATIO ? 1 : 0;
    const earnedXp = correct * 20 + (passed ? 30 : 0);
    const earnedCoins = correct * 10 + (stars === 3 ? 50 : 0);

    const nextProgress = JSON.parse(JSON.stringify(stateRef.current.progress));
    const nextStats = { ...stateRef.current.stats };
    nextStats.xp = (nextStats.xp || 0) + earnedXp;
    nextStats.coins = (nextStats.coins || 0) + earnedCoins;
    nextStats.gamesPlayed = (nextStats.gamesPlayed || 0) + 1;
    nextStats.bestStreak = Math.max(nextStats.bestStreak || 0, streak || 0);

    const levelIds = LEVELS.map((l) => l.id);
    const idx = levelIds.indexOf(difficultyId);
    const cell = nextProgress[topicId][difficultyId];
    if (passed) {
      cell.completed = true;
      cell.stars = Math.max(cell.stars, stars);
      cell.bestScore = Math.max(cell.bestScore, correct);
      if (idx >= 0 && idx < levelIds.length - 1) {
        nextProgress[topicId][levelIds[idx + 1]].unlocked = true;
      }
    }
    setProgress(nextProgress);
    setStats(nextStats);
    persist(nextProgress, nextStats);
    return { passed, stars, earnedXp, earnedCoins };
  }, [persist]);

  return (
    <ClientContext.Provider
      value={{
        clientId,
        client,
        loading,
        progress,
        stats,
        level,
        profile,
        knowledge,
        lastResult,
        setLastResult,
        recordResult,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export const useClient = () => useContext(ClientContext);
